import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import HeroBasic, {heroBasicSizes, heroBasicTitleSizes, heroBasicTitleTextAlign} from '@adv-mt/mt-hero-basic'
import Layout from '@adv-mt/mt-layout-basic'
import PerfDynamicRendering from '@adv-mt/mt-perf-dynamic-rendering'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import AccordionFaqs from './components/AccordionFaqs/index.js'
import CardsBenefits from './components/CardsBenefits/index.js'
import FormInstantOffer from './components/FormInstantOffer/index.js'
import TopBar from './components/TopBar/index.js'

const HERO_HEIGHT = 385
const BENEFIT_CARDS_HEIGHT = 395
const FAQS_HEIGHT = 550

const LandingInstantOffer = ({h1, helmet, pageName, sellVehicleConfigForm}) => {
  const {domain, i18n} = useContext(Context)
  const {user} = useContext(AuthenticationContext)

  const config = domain.get('config')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')

  const heroBasicProps = {
    contentCenter: true,
    imageSrc: `${URL_STATICS_IMAGES}coches/landing/instant-offer/hero.jpg`,
    title: {
      description: i18n.t(`INSTANT_OFFER.LANDING.MAKE_IT_SAFE`),
      size: heroBasicTitleSizes.l,
      title: i18n.t(h1),
      textAlign: heroBasicTitleTextAlign.center
    },
    size: heroBasicSizes.m,
    topbar: <TopBar />,
    withFullContrast: true
  }

  return (
    <Layout.FullWidth isHeaderless linkFactory={LinkFactory}>
      <Helmet {...helmet} />
      <div className="mt-LandingInstantOffer">
        <PerfDynamicRendering height={HERO_HEIGHT}>
          <HeroBasic {...heroBasicProps} />
        </PerfDynamicRendering>
        <FormInstantOffer currentUser={user} sellVehicleConfigForm={sellVehicleConfigForm} />
        <PerfDynamicRendering height={BENEFIT_CARDS_HEIGHT}>
          <CardsBenefits domain={domain} i18n={i18n} />
        </PerfDynamicRendering>
        <PerfDynamicRendering height={FAQS_HEIGHT}>
          <AccordionFaqs i18n={i18n} />
        </PerfDynamicRendering>
      </div>
      <TrackingPageView pageName={pageName} />
    </Layout.FullWidth>
  )
}

LandingInstantOffer.displayName = 'LandingInstantOffer'

LandingInstantOffer.propTypes = {
  h1: PropTypes.string.isRequired,
  helmet: PropTypes.object.isRequired,
  pageName: PropTypes.string.isRequired,
  sellVehicleConfigForm: PropTypes.object
}

LandingInstantOffer.getInitialProps = async ({routeInfo, context: {domain, i18n, pde}}) => {
  const {
    location: {pathname: path}
  } = routeInfo

  const {pageName} = await domain.get('get_page_name_page_use_case').execute({path})

  const getSeoDataPromise = domain.get('get_seo_data_use_case').execute({
    culture: i18n.culture,
    pageName
  })

  const getSellVehicleConfigFormPromise = domain
    .get('get_sell_vehicle_config_form_use_case')
    .execute({withAdditionalCarDataFields: true})

  const [
    {
      value: [, getSeoDataPromiseResponse]
    },
    {
      value: [, sellVehicleConfigForm]
    }
  ] = await Promise.allSettled([getSeoDataPromise, getSellVehicleConfigFormPromise])

  const initialData = {
    pageName,
    sellVehicleConfigForm,
    ...(getSeoDataPromiseResponse && {
      h1: getSeoDataPromiseResponse.h1,
      helmet: getSeoDataPromiseResponse.head || {}
    })
  }

  return initialData
}

export default LandingInstantOffer
